import React from 'react';

interface DownArrowIndicatorProps {
  /**
   * Whether to show the indicator
   */
  show: boolean;
  /**
   * Click handler for the indicator
   */
  onClick?: () => void;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Whether the indicator is clickable
   */
  clickable?: boolean;
  /**
   * Custom text to show below the arrow
   */
  text?: string;
  /**
   * Position of the indicator ('bottom-right' | 'bottom-center' | 'bottom-left')
   */
  position?: 'bottom-right' | 'bottom-center' | 'bottom-left';
}

/**
 * Down arrow indicator that shows when there's more content below
 * Can be clickable to scroll to bottom or just informational
 */
export const DownArrowIndicator: React.FC<DownArrowIndicatorProps> = ({
  show,
  onClick,
  className = '',
  clickable = true,
  text,
  position = 'bottom-center',
}) => {
  if (!show) {
    return null;
  }

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-center': 'bottom-0 inline-flex left-1/2 -translate-x-1/2',
    'bottom-left': 'bottom-4 left-4',
  };

  const baseClasses = `
    sticky z-50 flex items-center justify-center p-2 rounded-full
    bg-white dark:bg-gray-900 border shadow-xl border-gray-300 dark:border-gray-600
    transition-all duration-300 ease-in-out
    ${clickable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-xl' : 'cursor-default'}
    ${positionClasses[position]}
    ${className}
  `;

  const arrowClasses = `
    w-4 h-4 text-gray-900 dark:text-gray-100 transition-transform duration-300
    ${clickable ? 'hover:text-gray-800 hover:scale-110' : ''}
  `;

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  return (
    <div
      className={baseClasses}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      {/* Down arrow icon */}
      <svg
        className={arrowClasses}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19 14l-7 7m0 0l-7-7m7 7V3"
        />
      </svg>

      {/* Text label - only show if provided */}
      {text && (
        <span className="ml-2 text-xs font-medium whitespace-nowrap text-gray-600 dark:text-gray-400">
          {text}
        </span>
      )}
    </div>
  );
};
